/**
 * AudioPod Client
 * Main client class for the AudioPod API
 */

import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import FormData from 'form-data';
import {
  AudioPodConfig,
  ApiResponse,
  AudioPodError,
  Job,
  JobStatus,
  UploadProgress
} from '../types';
import { VoiceService } from '../services/VoiceService';
import { MusicService } from '../services/MusicService';
import { TranscriptionService } from '../services/TranscriptionService';
import { TranslationService } from '../services/TranslationService';
import { CreditService } from '../services/CreditService';
import { SpeakerService } from '../services/SpeakerService';
import { DenoiserService } from '../services/DenoiserService';
import { KaraokeService } from '../services/KaraokeService';
import { StemExtractionService } from '../services/StemExtractionService';

export class AudioPodClient {
  private readonly httpClient: AxiosInstance;
  private readonly config: Required<AudioPodConfig>;

  // Service instances
  public readonly voice: VoiceService;
  public readonly music: MusicService;
  public readonly transcription: TranscriptionService;
  public readonly translation: TranslationService;
  public readonly credits: CreditService;
  public readonly speaker: SpeakerService;
  public readonly denoiser: DenoiserService;
  public readonly karaoke: KaraokeService;
  public readonly stemExtraction: StemExtractionService;

  constructor(config: AudioPodConfig) {
    if (!config.apiKey) {
      throw new Error('API key is required. Get your API key from https://app.audiopod.ai/dashboard');
    }

    if (!config.apiKey.startsWith('ap_')) {
      throw new Error('Invalid API key format. AudioPod API keys start with "ap_"');
    }

    this.config = {
      apiKey: config.apiKey,
      baseURL: config.baseURL || 'https://api.audiopod.ai',
      timeout: config.timeout || 30000,
      maxRetries: config.maxRetries || 3,
      debug: config.debug || false
    };

    this.httpClient = this.createHttpClient();

    // Initialize services
    this.voice = new VoiceService(this);
    this.music = new MusicService(this);
    this.transcription = new TranscriptionService(this);
    this.translation = new TranslationService(this);
    this.credits = new CreditService(this);
    this.speaker = new SpeakerService(this);
    this.denoiser = new DenoiserService(this);
    this.karaoke = new KaraokeService(this);
    this.stemExtraction = new StemExtractionService(this);
  }

  private createHttpClient(): AxiosInstance {
    const client = axios.create({
      baseURL: `${this.config.baseURL}/api/v1`,
      timeout: this.config.timeout,
      headers: {
        'X-API-Key': this.config.apiKey,
        'Content-Type': 'application/json',
        'User-Agent': '@audiopod/sdk/1.0.0',
        'Accept': 'application/json'
      }
    });

    // Request interceptor for debugging
    if (this.config.debug) {
      client.interceptors.request.use(
        (config) => {
          console.log('AudioPod API Request:', {
            method: config.method?.toUpperCase(),
            url: config.url,
            headers: config.headers,
            data: config.data instanceof FormData ? '[FormData]' : config.data
          });
          return config;
        },
        (error) => {
          console.error('AudioPod API Request Error:', error);
          return Promise.reject(error);
        }
      );
    }

    // Response interceptor for error handling
    client.interceptors.response.use(
      (response: AxiosResponse) => {
        if (this.config.debug) {
          console.log('AudioPod API Response:', {
            status: response.status,
            data: response.data
          });
        }
        return response;
      },
      (error: AxiosError) => {
        const audioPodError = this.handleApiError(error);
        if (this.config.debug) {
          console.error('AudioPod API Error:', audioPodError);
        }
        return Promise.reject(audioPodError);
      }
    );

    return client;
  }

  private handleApiError(error: AxiosError): AudioPodError {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
      case 401:
        return {
          message: 'Invalid API key or authentication failed',
          code: 'AUTHENTICATION_ERROR',
          statusCode: 401
        };
      case 429:
        return {
          message: 'Rate limit exceeded. Please try again later.',
          code: 'RATE_LIMIT_ERROR',
          statusCode: 429
        };
      case 402:
        return {
          message: 'Insufficient credits',
          code: 'INSUFFICIENT_CREDITS',
          statusCode: 402
        };
      default:
        return {
          message: (data as any)?.detail || error.message || 'API request failed',
          code: 'API_ERROR',
          statusCode: status,
          details: data as any
        };
      }
    } else if (error.request) {
      return {
        message: 'Network error: Unable to reach AudioPod API',
        code: 'NETWORK_ERROR'
      };
    } else {
      return {
        message: error.message || 'Unknown error occurred',
        code: 'UNKNOWN_ERROR'
      };
    }
  }

  /**
   * Make a GET request to the API
   */
  async get<T = any>(endpoint: string, params?: Record<string, any>): Promise<T> {
    const response = await this.httpClient.get<ApiResponse<T>>(endpoint, { params });
    return (response.data as any).data || response.data as T;
  }

  /**
   * Make a POST request to the API
   */
  async post<T = any>(
    endpoint: string,
    data?: any,
    config?: any
  ): Promise<T> {
    const response = await this.httpClient.post<ApiResponse<T>>(endpoint, data, config);
    return (response.data as any).data || response.data as T;
  }

  /**
   * Make a PUT request to the API
   */
  async put<T = any>(endpoint: string, data?: any): Promise<T> {
    const response = await this.httpClient.put<ApiResponse<T>>(endpoint, data);
    return (response.data as any).data || response.data as T;
  }

  /**
   * Make a DELETE request to the API
   */
  async delete<T = any>(endpoint: string): Promise<T> {
    const response = await this.httpClient.delete<ApiResponse<T>>(endpoint);
    return (response.data as any).data || response.data as T;
  }

  /**
   * Upload a file to the API
   */
  async uploadFile<T = any>(
    endpoint: string,
    fileData: File | Blob,
    formData: Record<string, any> = {},
    onProgress?: (progress: UploadProgress) => void
  ): Promise<T> {
    const form = new FormData();

    // Add file
    if (typeof window !== 'undefined' && fileData instanceof File) {
      form.append('file', fileData, fileData.name);
    } else {
      form.append('file', fileData as Blob);
    }

    // Add other form fields
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        form.append(key, String(value));
      }
    });

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...form.getHeaders?.()
      },
      onUploadProgress: onProgress ? (progressEvent: any) => {
        const progress: UploadProgress = {
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total)
        };
        onProgress(progress);
      } : undefined
    };

    const response = await this.httpClient.post<ApiResponse<T>>(endpoint, form, config);
    return (response.data as any).data || response.data as T;
  }

  /**
   * Wait for a job to complete
   */
  async waitForJobCompletion<T = any>(
    jobId: number,
    timeout: number = 300000, // 5 minutes
    pollInterval: number = 5000, // 5 seconds
    onProgress?: (job: Job) => void
  ): Promise<T> {
    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
      try {
        const job = await this.getJobStatus(jobId);

        if (onProgress) {
          onProgress(job);
        }

        switch (job.status) {
        case JobStatus.COMPLETED:
          return (job.result || job) as T;
        case JobStatus.FAILED:
          throw new Error(`Job ${jobId} failed: ${job.errorMessage}`);
        case JobStatus.CANCELLED:
          throw new Error(`Job ${jobId} was cancelled`);
        case JobStatus.PENDING:
        case JobStatus.PROCESSING:
          // Continue polling
          break;
        default:
          throw new Error(`Unknown job status: ${job.status}`);
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval));
      } catch (error) {
        if ((error as AudioPodError).code === 'NOT_FOUND') {
          throw new Error(`Job ${jobId} not found`);
        }
        throw error;
      }
    }

    throw new Error(`Job ${jobId} timed out after ${timeout}ms`);
  }

  /**
   * Get job status
   */
  async getJobStatus(jobId: number): Promise<Job> {
    return this.get<Job>(`/jobs/${jobId}`);
  }

  /**
   * Cancel a job
   */
  async cancelJob(jobId: number): Promise<void> {
    await this.post(`/jobs/${jobId}/cancel`);
  }

  /**
   * Check API health
   */
  async checkHealth(): Promise<{ status: string; timestamp: string }> {
    return this.get('/health');
  }

  /**
   * Get current user information
   */
  async getUserInfo(): Promise<any> {
    return this.get('/auth/me');
  }

  /**
   * Get API configuration
   */
  getConfig(): Readonly<Required<AudioPodConfig>> {
    return { ...this.config };
  }

  /**
   * Update API key
   */
  updateApiKey(apiKey: string): void {
    if (!apiKey.startsWith('ap_')) {
      throw new Error('Invalid API key format. AudioPod API keys start with "ap_"');
    }

    this.config.apiKey = apiKey;
    this.httpClient.defaults.headers['X-API-Key'] = apiKey;
  }

  /**
   * Set debug mode
   */
  setDebug(enabled: boolean): void {
    this.config.debug = enabled;
  }
}
